import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { CompanyDeleteCredentialModel } from 'src/app/models/authentication/company-delete-credential.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-delete-company',
  templateUrl: './delete-company.component.html',
  styleUrls: ['./delete-company.component.scss'],
  standalone: false
})
export class DeleteCompanyComponent implements OnInit {

  companyId: number = 0;
  formDeleteCompany: FormGroup = new FormGroup({});
  subscription: Subscription;

  constructor(private confirmDialogService: ConfirmDialogService,
    public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    @Optional() private ref: DynamicDialogRef,
    private toastHandlerService: ToastHandlerService,
    private companyService: CompanyService) { }

  async ngOnInit() {
    this.validateForm();
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.companyId = inputs.companyId;
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  close() {
    const data: ModalObjectType = {};
    data.params = { reload: true };
    this.ref.close(data);
  }

  async deleteCompany() {
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar la compañía?')) {
      try {
        await firstValueFrom(this.companyService.deleteCompany(this.companyId));
        this.toastHandlerService.showToast('success', 'La compañía fue eliminada exitosamente.');
        this.close();
      } catch (error) {
        console.error('Error al eliminar la compañía.', error);
      }
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formDeleteCompany.valid) {
      return this.showValidationMessages();
    }

    const response = await this.validateDeleteCompany();
    if (response) {
      await this.deleteCompany();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      document: {
        required: 'El documento es obligatorio.'
      },
      passwordHash: {
        required: 'La contraseña es obligatoria.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formDeleteCompany.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formDeleteCompany = this.formBuilder.group({
      document: ['', Validators.required],
      passwordHash: [null, Validators.required]
    });
  }

  async validateDeleteCompany(): Promise<boolean> {
    try {
      const model = this.formDeleteCompany.value as CompanyDeleteCredentialModel;
      model.companyId = this.companyId;
      return await firstValueFrom(this.companyService.validateDeleteCompany(model));
    } catch (error) {
      console.error('Error validando credenciales para eliminación.', error);
      return false;
    }
  }

  validateFormControls() {
    Object.keys(this.formDeleteCompany.controls).forEach((key) => {
      const control = this.formDeleteCompany.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
